// Canonical enums shared by backend (Prisma) and every client.
// Keep in sync with apps/api/prisma/schema.prisma.

export const MembershipRole = {
  Owner: 'OWNER',
  Admin: 'ADMIN',
  Manager: 'MANAGER',
  Member: 'MEMBER',
} as const;
export type MembershipRole = (typeof MembershipRole)[keyof typeof MembershipRole];

export const MembershipStatus = {
  Active: 'ACTIVE',
  Invited: 'INVITED',
  Suspended: 'SUSPENDED',
} as const;
export type MembershipStatus = (typeof MembershipStatus)[keyof typeof MembershipStatus];

export const TaskStatus = {
  Active: 'ACTIVE',
  Done: 'DONE',
} as const;
export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

export const InvitationStatus = {
  Pending: 'PENDING',
  Accepted: 'ACCEPTED',
  Revoked: 'REVOKED',
  Expired: 'EXPIRED',
} as const;
export type InvitationStatus = (typeof InvitationStatus)[keyof typeof InvitationStatus];

export const ShareAccess = {
  ReadOnly: 'READ_ONLY',
} as const;
export type ShareAccess = (typeof ShareAccess)[keyof typeof ShareAccess];

export const PlanCode = {
  Free: 'FREE',
  Pro: 'PRO',
  Team: 'TEAM',
  Enterprise: 'ENTERPRISE',
} as const;
export type PlanCode = (typeof PlanCode)[keyof typeof PlanCode];

export const SubscriptionStatus = {
  Trialing: 'TRIALING',
  Active: 'ACTIVE',
  PastDue: 'PAST_DUE',
  Canceled: 'CANCELED',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export const DataExportStatus = {
  Pending: 'PENDING',
  Processing: 'PROCESSING',
  Ready: 'READY',
  Failed: 'FAILED',
} as const;
export type DataExportStatus = (typeof DataExportStatus)[keyof typeof DataExportStatus];

export const AccountDeletionStatus = {
  Requested: 'REQUESTED',
  GracePeriod: 'GRACE_PERIOD',
  Purged: 'PURGED',
  Cancelled: 'CANCELLED',
} as const;
export type AccountDeletionStatus =
  (typeof AccountDeletionStatus)[keyof typeof AccountDeletionStatus];

export const ExportFormat = {
  Csv: 'CSV',
  Pdf: 'PDF',
} as const;
export type ExportFormat = (typeof ExportFormat)[keyof typeof ExportFormat];

/**
 * Which shape of report an export renders: `SUMMARY` totals time by project
 * and task (and, in the PDF, by team member), `DETAILED` lists one row per
 * time entry. Independent of `ExportFormat` — every combination is valid.
 */
export const ExportReportType = {
  Summary: 'SUMMARY',
  Detailed: 'DETAILED',
} as const;
export type ExportReportType = (typeof ExportReportType)[keyof typeof ExportReportType];

export const ExportJobStatus = {
  Queued: 'QUEUED',
  Processing: 'PROCESSING',
  Ready: 'READY',
  Failed: 'FAILED',
} as const;
export type ExportJobStatus = (typeof ExportJobStatus)[keyof typeof ExportJobStatus];

export const ImportSource = {
  GenericCsv: 'GENERIC_CSV',
  Clockify: 'CLOCKIFY',
} as const;
export type ImportSource = (typeof ImportSource)[keyof typeof ImportSource];

export const ImportJobStatus = {
  Uploaded: 'UPLOADED',
  Previewed: 'PREVIEWED',
  Committed: 'COMMITTED',
  Failed: 'FAILED',
} as const;
export type ImportJobStatus = (typeof ImportJobStatus)[keyof typeof ImportJobStatus];

export const PointsReason = {
  TimeLogged: 'TIME_LOGGED',
  TaskCompleted: 'TASK_COMPLETED',
  StreakBonus: 'STREAK_BONUS',
  BadgeBonus: 'BADGE_BONUS',
  ChallengeBonus: 'CHALLENGE_BONUS',
} as const;
export type PointsReason = (typeof PointsReason)[keyof typeof PointsReason];

export const BadgeThresholdType = {
  FirstEntry: 'FIRST_ENTRY',
  StreakDays: 'STREAK_DAYS',
  TotalHours: 'TOTAL_HOURS',
  TasksCompleted: 'TASKS_COMPLETED',
} as const;
export type BadgeThresholdType = (typeof BadgeThresholdType)[keyof typeof BadgeThresholdType];

export const ChallengeMetric = {
  HoursLogged: 'HOURS_LOGGED',
  TasksCompleted: 'TASKS_COMPLETED',
} as const;
export type ChallengeMetric = (typeof ChallengeMetric)[keyof typeof ChallengeMetric];

/** Ordered weakest → strongest; used for role-comparison helpers. */
export const ROLE_ORDER: MembershipRole[] = [
  MembershipRole.Member,
  MembershipRole.Manager,
  MembershipRole.Admin,
  MembershipRole.Owner,
];

export function roleAtLeast(role: MembershipRole, minimum: MembershipRole): boolean {
  return ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(minimum);
}

/** Named feature flags gated by entitlements — kept in sync with docs/prd/13-monetization.md §5. */
export const FeatureFlag = {
  BillableRates: 'billable_rates',
  PdfExport: 'pdf_export',
  ScheduledReports: 'scheduled_reports',
  BulkEdit: 'bulk_edit',
  ManagerRole: 'manager_role',
  ProjectAccessControl: 'project_access_control',
  Approvals: 'approvals',
  Sso: 'sso',
  AuditLogAccess: 'audit_log_access',
  CustomSubdomain: 'custom_subdomain',
  SelfHosted: 'self_hosted',
} as const;
export type FeatureFlag = (typeof FeatureFlag)[keyof typeof FeatureFlag];
